import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { t } from '../lib/translations';

interface SectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function Section({ title, children, defaultOpen = false }: SectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="research-section card mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="section-toggle"
      >
        <h2 className="section-title">{title}</h2>
        <span className="section-icon">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && <div className="section-content">{children}</div>}
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <h3 className="text-lg font-semibold text-indigo-800 mb-2">{title}</h3>
      {children}
    </div>
  );
}

function AffirmationCard({ text }: { text: string }) {
  return (
    <div
      className="p-3 rounded-lg mb-2"
      style={{ background: 'linear-gradient(135deg, #e0e7ff 0%, #f3e8ff 100%)' }}
    >
      <p className="text-indigo-900 italic">"{text}"</p>
    </div>
  );
}

export function Research() {
  const { language } = useLanguage();

  const content = language === 'es' ? spanishContent : englishContent;

  return (
    <div className="research-page">
      {/* Navigation Header */}
      <nav className="research-nav">
        <div className="research-nav-inner">
          <Link to="/" className="back-link">
            ← {t('backToHome', language)}
          </Link>
          <Link to="/" className="nav-brand">
            ✨ MindsetFamily
          </Link>
          <div className="research-nav-actions">
            <LanguageSelector />
            <Link to="/signup" className="btn btn-primary" style={{ padding: '0.5rem 1rem' }}>
              {t('getStarted', language)}
            </Link>
          </div>
        </div>
      </nav>

      <div className="page-container">
        <div className="page-header">
          <div className="research-icon">🔬</div>
          <h1 className="page-title">{content.title}</h1>
          <p className="page-subtitle">{content.subtitle}</p>
        </div>

      <Section title={content.section1.title} defaultOpen={true}>
        <SubSection title={content.section1.neuroscience.title}>
          <div className="mb-4">
            <h4 className="font-semibold text-indigo-700 mb-2">{content.section1.neuroscience.sat.title}</h4>
            <ul className="list-disc pl-5 space-y-1 text-gray-700">
              {content.section1.neuroscience.sat.points.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          </div>
          <div className="mb-4">
            <h4 className="font-semibold text-indigo-700 mb-2">{content.section1.neuroscience.neuroplasticity.title}</h4>
            <ul className="list-disc pl-5 space-y-1 text-gray-700">
              {content.section1.neuroscience.neuroplasticity.points.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          </div>
        </SubSection>

        <SubSection title={content.section1.howItWorks.title}>
          <ol className="list-decimal pl-5 space-y-2 text-gray-700">
            {content.section1.howItWorks.points.map((point, i) => (
              <li key={i}><strong>{point.title}:</strong> {point.desc}</li>
            ))}
          </ol>
        </SubSection>

        <SubSection title={content.section1.caveat.title}>
          <p className="text-gray-700 p-3 bg-amber-50 rounded-lg border border-amber-200">
            {content.section1.caveat.text}
          </p>
        </SubSection>
      </Section>

      <Section title={content.section2.title}>
        {content.section2.techniques.map((tech, i) => (
          <SubSection key={i} title={tech.title}>
            <p className="text-gray-700 mb-2">{tech.desc}</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-3 bg-indigo-50 rounded-lg">
                <h5 className="font-semibold text-indigo-700 mb-2">{content.section2.forChildren}</h5>
                <ul className="list-disc pl-5 text-gray-700 text-sm">
                  {tech.forChildren.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <h5 className="font-semibold text-purple-700 mb-2">{content.section2.forFamily}</h5>
                <ul className="list-disc pl-5 text-gray-700 text-sm">
                  {tech.forFamily.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </SubSection>
        ))}
      </Section>

      <Section title={content.section3.title}>
        <SubSection title={content.section3.timing.title}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-indigo-100">
                  <th className="p-2 text-left">{content.section3.timing.headers[0]}</th>
                  <th className="p-2 text-left">{content.section3.timing.headers[1]}</th>
                  <th className="p-2 text-left">{content.section3.timing.headers[2]}</th>
                </tr>
              </thead>
              <tbody>
                {content.section3.timing.rows.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                    <td className="p-2 font-semibold">{row[0]}</td>
                    <td className="p-2">{row[1]}</td>
                    <td className="p-2">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SubSection>

        <SubSection title={content.section3.routine.title}>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-amber-50 rounded-lg">
              <h5 className="font-semibold text-amber-700 mb-2">{content.section3.routine.morning.title}</h5>
              <ol className="list-decimal pl-5 text-gray-700">
                {content.section3.routine.morning.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>
            <div className="p-4 bg-indigo-50 rounded-lg">
              <h5 className="font-semibold text-indigo-700 mb-2">{content.section3.routine.evening.title}</h5>
              <ol className="list-decimal pl-5 text-gray-700">
                {content.section3.routine.evening.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>
          </div>
        </SubSection>
      </Section>

      <Section title={content.section4.title}>
        {content.section4.ageGroups.map((group, i) => (
          <SubSection key={i} title={group.title}>
            <p className="text-gray-600 mb-3">{group.characteristics}</p>
            <h5 className="font-semibold text-indigo-700 mb-2">{content.section4.examples}</h5>
            <div className="grid md:grid-cols-2 gap-2">
              {group.affirmations.map((aff, j) => (
                <AffirmationCard key={j} text={aff} />
              ))}
            </div>
          </SubSection>
        ))}
      </Section>

      <Section title={content.section5.title}>
        <SubSection title={content.section5.framework.title}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            {content.section5.framework.items.map((item, i) => (
              <div key={i} className="p-3 bg-indigo-100 rounded-lg text-center">
                <span className="text-2xl font-bold text-indigo-600">{item.letter}</span>
                <p className="text-sm text-gray-700">{item.word}</p>
              </div>
            ))}
          </div>
        </SubSection>

        <SubSection title={content.section5.formulas.title}>
          {content.section5.formulas.items.map((formula, i) => (
            <div key={i} className="mb-3 p-3 bg-gray-50 rounded-lg">
              <p className="font-semibold text-indigo-700">{formula.pattern}</p>
              <p className="text-gray-600 text-sm">{formula.example}</p>
            </div>
          ))}
        </SubSection>

        <SubSection title={content.section5.believability.title}>
          <p className="text-gray-700 mb-3">{content.section5.believability.desc}</p>
          <div className="space-y-2">
            {content.section5.believability.levels.map((level, i) => (
              <div
                key={i}
                className="p-2 rounded-lg"
                style={{
                  background: `linear-gradient(90deg, ${['#fef3c7', '#fde68a', '#fcd34d', '#fbbf24', '#f59e0b'][i]} 0%, white 100%)`,
                }}
              >
                <span className="font-semibold">{content.section5.believability.levelLabel} {i + 1}:</span> {level}
              </div>
            ))}
          </div>
        </SubSection>
      </Section>

      <Section title={content.section6.title}>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-green-50 rounded-lg">
            <h4 className="font-semibold text-green-700 mb-3">{content.section6.dos.title}</h4>
            <ul className="space-y-2">
              {content.section6.dos.items.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-4 bg-red-50 rounded-lg">
            <h4 className="font-semibold text-red-700 mb-3">{content.section6.donts.title}</h4>
            <ul className="space-y-2">
              {content.section6.donts.items.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-red-600">✗</span>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

        {/* CTA Footer */}
        <div className="research-cta">
          <h3>{content.title}</h3>
          <p>{t('ctaSubtitle', language)}</p>
          <Link to="/signup" className="btn btn-primary btn-large">
            {t('startJourney', language)}
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="research-footer">
        <p>{t('footerTagline', language)}</p>
        <p>© {new Date().getFullYear()} MindsetFamily</p>
      </footer>
    </div>
  );
}

const englishContent = {
  title: 'NLP & Affirmations Research',
  subtitle: 'The science behind positive affirmations and how to use them effectively',

  section1: {
    title: '1. Scientific Basis of Affirmations',
    neuroscience: {
      title: 'The Neuroscience Behind Affirmations',
      sat: {
        title: 'Self-Affirmation Theory (Steele, 1988)',
        points: [
          'People are motivated to maintain a positive self-image',
          'When our self-concept is threatened, affirmations help restore psychological equilibrium',
          'Research shows self-affirmation activates the brain\'s reward centers',
        ],
      },
      neuroplasticity: {
        title: 'Neuroplasticity',
        points: [
          'The brain can form new neural pathways through repeated practice',
          'Regular repetition of positive statements reshapes thought patterns',
          'fMRI studies show affirmations activate self-processing brain regions',
        ],
      },
    },
    howItWorks: {
      title: 'How Affirmations Work',
      points: [
        { title: 'Cognitive Restructuring', desc: 'Replaces negative automatic thoughts with positive alternatives' },
        { title: 'Reticular Activating System', desc: 'Programs the brain to notice opportunities aligned with beliefs' },
        { title: 'Self-Perception Theory', desc: 'Behavior follows belief; stating positive beliefs influences actions' },
        { title: 'Stress Reduction', desc: 'Studies show affirmations can lower cortisol (stress hormone) levels' },
      ],
    },
    caveat: {
      title: 'Important Note',
      text: 'Research shows affirmations work best for people with moderate-to-high self-esteem. For those with low self-esteem, use "bridging" affirmations that are believable: "I am learning to..." rather than "I am perfect at..."',
    },
  },

  section2: {
    title: '2. NLP Techniques for Families',
    forChildren: 'For Children',
    forFamily: 'For Family',
    techniques: [
      {
        title: 'Anchoring',
        desc: 'Creating positive emotional associations with specific triggers (gestures, words, or symbols).',
        forChildren: ['Power poses (hands on hips like a superhero)', 'Special handshakes before challenges', '"Confidence buttons" - touching a body part when saying an affirmation'],
        forFamily: ['Family motto or chant before big events', 'Morning ritual gestures', '"High-five and affirm" routine'],
      },
      {
        title: 'Reframing',
        desc: 'Changing the perspective on a situation to find positive meaning.',
        forChildren: ['"Not yet" language: "I can\'t do this" becomes "I can\'t do this YET"', 'Finding the lesson: "What did this teach me?"', 'Story reframing: "You\'re the hero who overcame..."'],
        forFamily: ['Family "silver lining" discussions', 'Reframing mistakes as "learning adventures"'],
      },
      {
        title: 'Modeling',
        desc: 'Learning by observing successful behaviors.',
        forChildren: ['"Who do you know that\'s good at this?"', 'Character modeling: "What would [hero] do?"', 'Parent demonstration of positive self-talk'],
        forFamily: ['Parents model affirmation practice', 'Siblings encourage each other'],
      },
      {
        title: 'VAK (Visual/Auditory/Kinesthetic)',
        desc: 'Engaging multiple senses in affirmation practice.',
        forChildren: ['Drawing affirmations', 'Singing or chanting', 'Movement-based affirmations'],
        forFamily: ['Creating vision boards together', 'Recording family affirmations', 'Morning movement routine'],
      },
    ],
  },

  section3: {
    title: '3. Daily Practice Best Practices',
    timing: {
      title: 'Optimal Timing Windows',
      headers: ['Time', 'Benefit', 'Best For'],
      rows: [
        ['Morning (upon waking)', 'Brain in theta/alpha state, highly receptive', 'Setting intentions, energy affirmations'],
        ['Before bed', 'Subconscious processes during sleep', 'Gratitude, self-worth affirmations'],
        ['Transition moments', 'Reset mindset for new context', 'Confidence, capability affirmations'],
        ['During stress', 'Interrupts negative thought loops', 'Calming, grounding affirmations'],
      ],
    },
    routine: {
      title: 'Recommended Daily Structure',
      morning: {
        title: 'Morning Routine (5-10 min)',
        steps: ['3 deep breaths', 'Gratitude statement', '3-5 core identity affirmations', 'Intention for the day'],
      },
      evening: {
        title: 'Evening Routine (5 min)',
        steps: ['Review wins from the day', 'Self-compassion affirmation', 'Growth/learning affirmation', 'Peaceful sleep affirmation'],
      },
    },
  },

  section4: {
    title: '4. Age-Appropriate Affirmations',
    examples: 'Example Affirmations',
    ageGroups: [
      {
        title: 'Children (5-8 years)',
        characteristics: 'Concrete thinking, short attention span, learn through play and repetition. Use simple, short sentences (5-8 words) with rhymes and movement.',
        affirmations: [
          'I am brave and strong!',
          'I can try new things',
          'My feelings are okay',
          'I am a good friend',
          'I can do hard things',
          'I am loved just as I am',
        ],
      },
      {
        title: 'Children (9-12 years)',
        characteristics: 'Developing abstract thinking, growing self-awareness, want to feel competent. Include achievement themes and acknowledge effort.',
        affirmations: [
          'I have unique talents and abilities',
          'I learn from my mistakes and keep going',
          'I choose kindness, including to myself',
          'I don\'t have to be perfect to be amazing',
          'I focus on progress, not perfection',
          'I have the power to make good choices',
        ],
      },
      {
        title: 'Teenagers (13-17 years)',
        characteristics: 'Abstract thinking developed, identity formation critical, need autonomy. Use sophisticated vocabulary, focus on growth mindset.',
        affirmations: [
          'I am worthy of respect, including my own',
          'I define my own success',
          'I can feel anxious and still take action',
          'Setbacks are setups for comebacks',
          'I am allowed to set boundaries',
          'My mental health is a priority',
        ],
      },
      {
        title: 'Adults (18+)',
        characteristics: 'Complex life responsibilities, varied needs. Balance aspiration with self-compassion, include values alignment.',
        affirmations: [
          'I am worthy of success, love, and happiness',
          'I bring unique value to my work',
          'I am the right parent for my child',
          'I embrace change as an opportunity for growth',
          'I am doing my best, and that is enough',
          'I am the author of my own story',
        ],
      },
    ],
  },

  section5: {
    title: '5. Structure of Effective Affirmations',
    framework: {
      title: 'The PPPPP Framework',
      items: [
        { letter: 'P', word: 'Personal' },
        { letter: 'P', word: 'Present' },
        { letter: 'P', word: 'Positive' },
        { letter: 'P', word: 'Powerful' },
        { letter: 'P', word: 'Precise' },
      ],
    },
    formulas: {
      title: 'Affirmation Formulas',
      items: [
        { pattern: '"I am [positive quality]"', example: 'Example: "I am resilient" / "I am creative"' },
        { pattern: '"I can [action/ability]"', example: 'Example: "I can handle this" / "I can learn anything"' },
        { pattern: '"I am allowed to [behavior/feeling]"', example: 'Example: "I am allowed to rest" / "I am allowed to succeed"' },
        { pattern: '"I am becoming [quality]"', example: 'Example: "I am becoming more confident every day"' },
        { pattern: '"Even when [challenge], I [positive truth]"', example: 'Example: "Even when things are hard, I persist"' },
      ],
    },
    believability: {
      title: 'The Believability Spectrum',
      desc: 'When affirmations feel too far from reality, use progressive statements:',
      levelLabel: 'Level',
      levels: [
        '"I am open to the possibility that I could be confident"',
        '"I am willing to believe I can be confident"',
        '"I am learning to be more confident"',
        '"I am becoming more confident every day"',
        '"I am confident"',
      ],
    },
  },

  section6: {
    title: '6. Key Principles Summary',
    dos: {
      title: "Do's",
      items: [
        'Use first person, present tense',
        'Frame positively (what you want)',
        'Make it believable (use bridging if needed)',
        'Engage emotion, not just words',
        'Practice consistently',
        'Pair with visualization',
        'Track progress and celebrate',
        'Personalize to the individual',
      ],
    },
    donts: {
      title: "Don'ts",
      items: [
        'Use future tense ("I will...")',
        'Use negatives ("I don\'t...")',
        'Make it unrealistic',
        'Rush through mechanically',
        'Skip days inconsistently',
        'Use the same affirmations forever',
        'Force affirmations that feel wrong',
      ],
    },
  },
};

const spanishContent = {
  title: 'Investigación de PNL y Afirmaciones',
  subtitle: 'La ciencia detrás de las afirmaciones positivas y cómo usarlas efectivamente',

  section1: {
    title: '1. Base Científica de las Afirmaciones',
    neuroscience: {
      title: 'La Neurociencia Detrás de las Afirmaciones',
      sat: {
        title: 'Teoría de la Autoafirmación (Steele, 1988)',
        points: [
          'Las personas están motivadas a mantener una autoimagen positiva',
          'Cuando nuestro autoconcepto está amenazado, las afirmaciones ayudan a restaurar el equilibrio psicológico',
          'La investigación muestra que la autoafirmación activa los centros de recompensa del cerebro',
        ],
      },
      neuroplasticity: {
        title: 'Neuroplasticidad',
        points: [
          'El cerebro puede formar nuevas vías neuronales a través de la práctica repetida',
          'La repetición regular de declaraciones positivas remodela los patrones de pensamiento',
          'Los estudios de fMRI muestran que las afirmaciones activan regiones cerebrales de autoprocesamiento',
        ],
      },
    },
    howItWorks: {
      title: 'Cómo Funcionan las Afirmaciones',
      points: [
        { title: 'Reestructuración Cognitiva', desc: 'Reemplaza pensamientos negativos automáticos con alternativas positivas' },
        { title: 'Sistema de Activación Reticular', desc: 'Programa el cerebro para notar oportunidades alineadas con las creencias' },
        { title: 'Teoría de la Autopercepción', desc: 'El comportamiento sigue a la creencia; declarar creencias positivas influye en las acciones' },
        { title: 'Reducción del Estrés', desc: 'Los estudios muestran que las afirmaciones pueden reducir los niveles de cortisol' },
      ],
    },
    caveat: {
      title: 'Nota Importante',
      text: 'La investigación muestra que las afirmaciones funcionan mejor para personas con autoestima moderada-alta. Para aquellos con baja autoestima, usa afirmaciones "puente" que sean creíbles: "Estoy aprendiendo a..." en lugar de "Soy perfecto en..."',
    },
  },

  section2: {
    title: '2. Técnicas de PNL para Familias',
    forChildren: 'Para Niños',
    forFamily: 'Para la Familia',
    techniques: [
      {
        title: 'Anclaje',
        desc: 'Crear asociaciones emocionales positivas con disparadores específicos (gestos, palabras o símbolos).',
        forChildren: ['Poses de poder (manos en la cadera como superhéroe)', 'Apretones de manos especiales antes de desafíos', '"Botones de confianza" - tocar una parte del cuerpo al decir una afirmación'],
        forFamily: ['Lema o canto familiar antes de grandes eventos', 'Gestos rituales matutinos', 'Rutina de "chocar los cinco y afirmar"'],
      },
      {
        title: 'Reencuadre',
        desc: 'Cambiar la perspectiva de una situación para encontrar un significado positivo.',
        forChildren: ['Lenguaje "todavía no": "No puedo hacer esto" se convierte en "No puedo hacer esto TODAVÍA"', 'Encontrar la lección: "¿Qué me enseñó esto?"', 'Reencuadre de historia: "Eres el héroe que superó..."'],
        forFamily: ['Discusiones familiares del "lado positivo"', 'Reencuadrar errores como "aventuras de aprendizaje"'],
      },
      {
        title: 'Modelado',
        desc: 'Aprender observando comportamientos exitosos.',
        forChildren: ['"¿Quién conoces que sea bueno en esto?"', 'Modelado de personajes: "¿Qué haría [héroe]?"', 'Demostración parental de autocharla positiva'],
        forFamily: ['Los padres modelan la práctica de afirmaciones', 'Los hermanos se animan mutuamente'],
      },
      {
        title: 'VAK (Visual/Auditivo/Kinestésico)',
        desc: 'Involucrar múltiples sentidos en la práctica de afirmaciones.',
        forChildren: ['Dibujar afirmaciones', 'Cantar o corear', 'Afirmaciones basadas en movimiento'],
        forFamily: ['Crear tableros de visión juntos', 'Grabar afirmaciones familiares', 'Rutina de movimiento matutino'],
      },
    ],
  },

  section3: {
    title: '3. Mejores Prácticas Diarias',
    timing: {
      title: 'Momentos Óptimos',
      headers: ['Momento', 'Beneficio', 'Mejor Para'],
      rows: [
        ['Mañana (al despertar)', 'Cerebro en estado theta/alfa, muy receptivo', 'Establecer intenciones, afirmaciones de energía'],
        ['Antes de dormir', 'El subconsciente procesa durante el sueño', 'Gratitud, afirmaciones de autoestima'],
        ['Momentos de transición', 'Reiniciar mentalidad para nuevo contexto', 'Confianza, afirmaciones de capacidad'],
        ['Durante el estrés', 'Interrumpe ciclos de pensamiento negativo', 'Afirmaciones calmantes, de conexión a tierra'],
      ],
    },
    routine: {
      title: 'Estructura Diaria Recomendada',
      morning: {
        title: 'Rutina Matutina (5-10 min)',
        steps: ['3 respiraciones profundas', 'Declaración de gratitud', '3-5 afirmaciones de identidad', 'Intención para el día'],
      },
      evening: {
        title: 'Rutina Nocturna (5 min)',
        steps: ['Revisar logros del día', 'Afirmación de autocompasión', 'Afirmación de crecimiento', 'Afirmación de sueño tranquilo'],
      },
    },
  },

  section4: {
    title: '4. Afirmaciones Apropiadas por Edad',
    examples: 'Ejemplos de Afirmaciones',
    ageGroups: [
      {
        title: 'Niños (5-8 años)',
        characteristics: 'Pensamiento concreto, atención corta, aprenden a través del juego y repetición. Usa oraciones simples y cortas (5-8 palabras) con rimas y movimiento.',
        affirmations: [
          '¡Soy valiente y fuerte!',
          'Puedo intentar cosas nuevas',
          'Mis sentimientos están bien',
          'Soy un buen amigo',
          'Puedo hacer cosas difíciles',
          'Soy amado tal como soy',
        ],
      },
      {
        title: 'Niños (9-12 años)',
        characteristics: 'Desarrollando pensamiento abstracto, creciente autoconciencia, quieren sentirse competentes. Incluye temas de logro y reconoce el esfuerzo.',
        affirmations: [
          'Tengo talentos y habilidades únicos',
          'Aprendo de mis errores y sigo adelante',
          'Elijo la bondad, incluyendo hacia mí mismo',
          'No tengo que ser perfecto para ser increíble',
          'Me enfoco en el progreso, no en la perfección',
          'Tengo el poder de tomar buenas decisiones',
        ],
      },
      {
        title: 'Adolescentes (13-17 años)',
        characteristics: 'Pensamiento abstracto desarrollado, formación de identidad crítica, necesitan autonomía. Usa vocabulario sofisticado, enfócate en mentalidad de crecimiento.',
        affirmations: [
          'Merezco respeto, incluyendo el mío propio',
          'Yo defino mi propio éxito',
          'Puedo sentir ansiedad y aún así actuar',
          'Los contratiempos son preparación para regresos',
          'Tengo permitido establecer límites',
          'Mi salud mental es una prioridad',
        ],
      },
      {
        title: 'Adultos (18+)',
        characteristics: 'Responsabilidades complejas de vida, necesidades variadas. Equilibra aspiración con autocompasión, incluye alineación de valores.',
        affirmations: [
          'Merezco éxito, amor y felicidad',
          'Aporto valor único a mi trabajo',
          'Soy el padre/madre adecuado para mi hijo',
          'Abrazo el cambio como oportunidad de crecimiento',
          'Estoy haciendo lo mejor que puedo, y eso es suficiente',
          'Soy el autor de mi propia historia',
        ],
      },
    ],
  },

  section5: {
    title: '5. Estructura de Afirmaciones Efectivas',
    framework: {
      title: 'El Marco PPPPP',
      items: [
        { letter: 'P', word: 'Personal' },
        { letter: 'P', word: 'Presente' },
        { letter: 'P', word: 'Positivo' },
        { letter: 'P', word: 'Poderoso' },
        { letter: 'P', word: 'Preciso' },
      ],
    },
    formulas: {
      title: 'Fórmulas de Afirmación',
      items: [
        { pattern: '"Soy [cualidad positiva]"', example: 'Ejemplo: "Soy resiliente" / "Soy creativo"' },
        { pattern: '"Puedo [acción/habilidad]"', example: 'Ejemplo: "Puedo manejar esto" / "Puedo aprender cualquier cosa"' },
        { pattern: '"Tengo permitido [comportamiento/sentimiento]"', example: 'Ejemplo: "Tengo permitido descansar" / "Tengo permitido tener éxito"' },
        { pattern: '"Me estoy convirtiendo en [cualidad]"', example: 'Ejemplo: "Me estoy volviendo más seguro cada día"' },
        { pattern: '"Incluso cuando [desafío], yo [verdad positiva]"', example: 'Ejemplo: "Incluso cuando las cosas son difíciles, persisto"' },
      ],
    },
    believability: {
      title: 'El Espectro de Credibilidad',
      desc: 'Cuando las afirmaciones se sienten muy lejanas de la realidad, usa declaraciones progresivas:',
      levelLabel: 'Nivel',
      levels: [
        '"Estoy abierto a la posibilidad de que podría ser seguro"',
        '"Estoy dispuesto a creer que puedo ser seguro"',
        '"Estoy aprendiendo a ser más seguro"',
        '"Me estoy volviendo más seguro cada día"',
        '"Soy seguro"',
      ],
    },
  },

  section6: {
    title: '6. Resumen de Principios Clave',
    dos: {
      title: 'Hacer',
      items: [
        'Usar primera persona, tiempo presente',
        'Enmarcar positivamente (lo que quieres)',
        'Hacerlo creíble (usar puente si es necesario)',
        'Involucrar emoción, no solo palabras',
        'Practicar consistentemente',
        'Combinar con visualización',
        'Seguir progreso y celebrar',
        'Personalizar al individuo',
      ],
    },
    donts: {
      title: 'No Hacer',
      items: [
        'Usar tiempo futuro ("Voy a...")',
        'Usar negativos ("No soy...")',
        'Hacerlo poco realista',
        'Apresurarse mecánicamente',
        'Saltar días inconsistentemente',
        'Usar las mismas afirmaciones siempre',
        'Forzar afirmaciones que se sienten mal',
      ],
    },
  },
};
